import { useEffect, useMemo, useState } from 'react'
import { apiAssetUrl } from '../config'
import { uploadAdminMedia, type MediaPurpose } from './media'

type Props = {
  /** Display URL or stored path */
  value: string | null | undefined
  /** Called with CDN stored path (cdn:...) or local path, plus display URL */
  onChange: (next: { storedPath: string | null; url: string }) => void
  label?: string
  aspectClass?: string
  className?: string
  /** Routes logo uploads through PNG + transparency-safe CDN path */
  purpose?: MediaPurpose
  /** Recommended size / format tip shown under the control */
  hint?: string
  /** object-fit for preview (logos should contain, covers cover) */
  previewFit?: 'cover' | 'contain'
}

export default function ImageUpload({
  value,
  onChange,
  label = 'Image',
  aspectClass = 'aspect-[16/10]',
  className = '',
  purpose = 'default',
  hint,
  previewFit = 'cover',
}: Props) {
  const [busy, setBusy] = useState(false)
  const [localPreview, setLocalPreview] = useState<string>('')
  const [err, setErr] = useState<string | null>(null)

  const preview = useMemo(() => {
    if (localPreview) return localPreview
    if (!value) return ''
    return apiAssetUrl(value)
  }, [localPreview, value])

  useEffect(() => {
    if (!localPreview.startsWith('blob:')) return
    return () => URL.revokeObjectURL(localPreview)
  }, [localPreview])

  async function onFile(file: File | null | undefined) {
    if (!file) return
    setErr(null)
    setBusy(true)
    const blobUrl = URL.createObjectURL(file)
    setLocalPreview(blobUrl)
    try {
      const up = await uploadAdminMedia(file, purpose)
      onChange({ storedPath: up.storedPath, url: up.url })
      setLocalPreview('')
    } catch (e) {
      setLocalPreview('')
      setErr(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  const fitClass = previewFit === 'contain' ? 'object-contain p-3 bg-transparent' : 'object-cover'

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
        {preview && (
          <button
            type="button"
            className="text-[0.7rem] font-semibold text-slate-400 hover:text-red-600"
            disabled={busy}
            onClick={() => {
              setLocalPreview('')
              onChange({ storedPath: null, url: '' })
            }}
          >
            Remove
          </button>
        )}
      </div>
      <label
        className={`group relative flex cursor-pointer overflow-hidden rounded-2xl border border-primary/15 bg-slate-50 transition hover:border-primary/35 hover:shadow-sm ${aspectClass} ${
          busy ? 'opacity-70 pointer-events-none' : ''
        }`}
      >
        {preview ? (
          <>
            <img src={preview} alt="" className={`h-full w-full ${fitClass}`} />
            <span className="absolute inset-0 flex items-center justify-center bg-navy-900/0 text-xs font-semibold text-white opacity-0 transition group-hover:bg-navy-900/45 group-hover:opacity-100">
              {busy ? 'Uploading…' : 'Change image'}
            </span>
          </>
        ) : (
          <span className="flex h-full w-full flex-col items-center justify-center gap-1.5 px-3 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary text-lg font-bold">
              +
            </span>
            <span className="text-sm font-semibold text-primary">
              {busy ? 'Uploading…' : 'Upload image'}
            </span>
            <span className="text-[0.7rem] text-slate-400">
              {purpose === 'logo' ? 'PNG preferred · transparent OK' : 'PNG / JPG · CDN'}
            </span>
          </span>
        )}
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={busy}
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
      </label>
      {hint && (
        <p className="rounded-xl border border-primary/10 bg-primary/[0.03] px-2.5 py-2 text-[0.7rem] leading-relaxed text-slate-600">
          <span className="font-semibold text-primary">Best size: </span>
          {hint}
        </p>
      )}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  )
}
